// What is under the cursor on the Look Editor canvas, as pure functions of
// formData and the preview size. ONE implementation, shared by the drag hook
// (click, hover, cursor) -- and it walks the SAME layer order the canvas
// draws in, top layer first.
//
// Before this the hit test had a fixed precedence (mascot, then photos, then
// text) that ignored layer_order. On a slide where a caption sits over a
// magnet photo, every click on the caption selected the photo under it; a
// photo stacked under another by Send to Back still took the click. The
// canvas showed one thing and the click chose another.
import { syncLayerOrder } from './LayerPanel';

const EDGE_GRAB = 6; // px grab zone for text-field edge resize
const HANDLE = 7; // px grab zone for resize/rotate handles
const ROTATE_HANDLE_OFFSET = 24; // px distance of rotation handle from oval edge

// The face oval is drawn only on a single-photo look (LookCanvas); a collage
// or gif slide that still carries eyes_y from an earlier life must not hit
// an invisible oval.
export const faceInteractive = (formData) => formData.look_type === 'single';

export const getFaceGeom = (formData, previewW, previewH) => {
  if (formData.eyes_y == null || formData.face_height == null || formData.center_x == null) return null;

  const cx = formData.center_x * previewW;
  const ey = formData.eyes_y * previewH;
  const fh = formData.face_height * previewH;
  const ovalCY = ey + fh * 0.1;
  return {
    cx, ey, ovalCY, fh, rx: fh * 0.35, ry: fh / 2, rotation: ((formData.rotation || 0) * Math.PI) / 180,
  };
};

// Mouse coords in the face oval's local (unrotated) frame.
export const toLocalFace = (mx, my, geom) => {
  const dx = mx - geom.cx;
  const dy = my - geom.ovalCY;
  const cos = Math.cos(-geom.rotation);
  const sin = Math.sin(-geom.rotation);
  return { lx: cos * dx - sin * dy, ly: sin * dx + cos * dy };
};

const layerKey = (layer) => (layer.index != null ? `${layer.type}:${layer.index}` : layer.type);

// Every element with its bounding box in preview pixels, keyed like a layer.
// Also the source for alignment guides, which is why it stays a list.
export const getElements = (formData, previewW, previewH) => {
  const els = [];
  const pctToX = (pct) => (pct || 0) * previewW;
  const pctToY = (pct) => (pct || 0) * previewH;

  const geom = getFaceGeom(formData, previewW, previewH);
  if (geom && faceInteractive(formData)) {
    els.push({
      type: 'face',
      centerX: geom.cx,
      centerY: geom.ovalCY,
      top: geom.ovalCY - geom.ry,
      bottom: geom.ovalCY + geom.ry,
      left: geom.cx - geom.rx,
      right: geom.cx + geom.rx,
      geom,
    });
  }

  if (formData.mascot_center_x != null && formData.mascot_center_y != null && formData.mascot_size != null) {
    const mx = pctToX(formData.mascot_center_x);
    const my = pctToY(formData.mascot_center_y);
    const ms = pctToX(formData.mascot_size);
    els.push({
      type: 'mascot',
      centerX: mx,
      centerY: my,
      top: my - ms / 2,
      bottom: my + ms / 2,
      left: mx - ms / 2,
      right: mx + ms / 2,
      halfSize: ms / 2,
    });
  }

  // Hit-test region matches the visible outline: half-height derived from
  // font_size_max scaled to the preview (min 12px so tiny fonts are still
  // clickable). Keep in sync with the box draw in LookCanvas.jsx.
  const canvasH = formData.canvas_height || 3000;
  (formData.text_fields || []).forEach((tf, i) => {
    if (tf.center_x == null || tf.center_y == null) return;
    const tx = pctToX(tf.center_x);
    const ty = pctToY(tf.center_y);
    const halfHeight = Math.max(12, ((tf.font_size_max || 160) * previewH * 0.7) / canvasH);
    els.push({
      type: 'text',
      index: i,
      centerX: tx,
      centerY: ty,
      top: ty - halfHeight,
      bottom: ty + halfHeight,
      left: pctToX(tf.bound_x_left || 0),
      right: pctToX(tf.bound_x_right || 1),
    });
  });

  (formData.collage_photos || []).forEach((photo, i) => {
    if (photo.x == null || photo.y == null || photo.width == null || photo.height == null) return;
    const px = pctToX(photo.x);
    const py = pctToY(photo.y);
    const pw = pctToX(photo.width);
    const ph = pctToY(photo.height);
    els.push({
      type: 'collage_photo',
      index: i,
      centerX: px + pw / 2,
      centerY: py + ph / 2,
      top: py,
      bottom: py + ph,
      left: px,
      right: px + pw,
      width: pw,
      height: ph,
    });
  });

  return els;
};

const near = (a, b, tol) => Math.abs(a - b) <= tol;

const hitFaceHandles = (mx, my, geom) => {
  const { lx, ly } = toLocalFace(mx, my, geom);
  if (near(lx, 0, HANDLE + 2) && near(ly, -geom.ry - ROTATE_HANDLE_OFFSET, HANDLE + 2)) {
    return { type: 'face', action: 'rotate' };
  }
  if (near(lx, 0, HANDLE + 4) && near(ly, -geom.ry, HANDLE)) return { type: 'face', action: 'resize-top' };
  if (near(lx, 0, HANDLE + 4) && near(ly, geom.ry, HANDLE)) return { type: 'face', action: 'resize-bottom' };
  if ((near(lx, -geom.rx, HANDLE) || near(lx, geom.rx, HANDLE)) && near(ly, 0, HANDLE + 4)) {
    return { type: 'face', action: 'resize-side' };
  }
  return null;
};

const hitFaceBody = (mx, my, geom) => {
  const { lx, ly } = toLocalFace(mx, my, geom);
  const nx = lx / (geom.rx + 8);
  const ny = ly / (geom.ry + 8);
  return nx * nx + ny * ny <= 1 ? { type: 'face', action: 'move' } : null;
};

const hitMascot = (mx, my, el) => {
  const corners = [
    { x: el.left, y: el.top }, { x: el.right, y: el.top },
    { x: el.left, y: el.bottom }, { x: el.right, y: el.bottom },
  ];
  if (corners.some((c) => near(mx, c.x, HANDLE + 2) && near(my, c.y, HANDLE + 2))) {
    return { type: 'mascot', action: 'resize' };
  }
  if (mx >= el.left - 4 && mx <= el.right + 4 && my >= el.top - 4 && my <= el.bottom + 4) {
    return { type: 'mascot', action: 'move' };
  }
  return null;
};

const hitPhoto = (mx, my, el) => {
  const base = { type: 'collage_photo', index: el.index };
  const corners = [
    { x: el.left, y: el.top, corner: 'tl' },
    { x: el.right, y: el.top, corner: 'tr' },
    { x: el.left, y: el.bottom, corner: 'bl' },
    { x: el.right, y: el.bottom, corner: 'br' },
  ];
  const corner = corners.find((c) => near(mx, c.x, HANDLE + 2) && near(my, c.y, HANDLE + 2));
  if (corner) return { ...base, action: 'resize', corner: corner.corner };

  const withinY = my > el.top + HANDLE && my < el.bottom - HANDLE;
  const withinX = mx > el.left + HANDLE && mx < el.right - HANDLE;
  if (near(mx, el.left, HANDLE) && withinY) return { ...base, action: 'resize-edge', edge: 'left' };
  if (near(mx, el.right, HANDLE) && withinY) return { ...base, action: 'resize-edge', edge: 'right' };
  if (near(my, el.top, HANDLE) && withinX) return { ...base, action: 'resize-edge', edge: 'top' };
  if (near(my, el.bottom, HANDLE) && withinX) return { ...base, action: 'resize-edge', edge: 'bottom' };
  if (mx >= el.left && mx <= el.right && my >= el.top && my <= el.bottom) return { ...base, action: 'move' };
  return null;
};

const hitText = (mx, my, el) => {
  if (my < el.top - 4 || my > el.bottom + 4) return null;
  const base = { type: 'text', index: el.index };
  if (near(mx, el.left, EDGE_GRAB)) return { ...base, action: 'resize-left' };
  if (near(mx, el.right, EDGE_GRAB)) return { ...base, action: 'resize-right' };
  if (mx >= el.left && mx <= el.right) return { ...base, action: 'move' };
  return null;
};

// The element under (mx, my) and what grabbing it there does, or null.
//
// Face handles (rotate / resize) win outright: they sit outside the oval and
// are the only way to reach those actions. Everything else is tested in the
// canvas's own stacking order, top layer first, so the click lands on what
// the designer sees in front -- the same syncLayerOrder LookCanvas draws by
// and LayerPanel lists.
export const hitTest = (formData, previewW, previewH, mx, my) => {
  const elements = getElements(formData, previewW, previewH);
  const byKey = new Map(elements.map((el) => [layerKey(el), el]));

  const face = byKey.get('face');
  if (face) {
    const handle = hitFaceHandles(mx, my, face.geom);
    if (handle) return handle;
  }

  const layers = syncLayerOrder(formData.layer_order, formData);
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    const el = byKey.get(layerKey(layers[i]));
    if (el) {
      let hit = null;
      if (el.type === 'face') hit = hitFaceBody(mx, my, el.geom);
      else if (el.type === 'mascot') hit = hitMascot(mx, my, el);
      else if (el.type === 'collage_photo') hit = hitPhoto(mx, my, el);
      else if (el.type === 'text') hit = hitText(mx, my, el);
      if (hit) return hit;
    }
  }
  return null;
};
